import { computed, watch, } from 'vue';
import { timeZoneCode } from '@/constants/global';
import { BusinessError } from '@/error';
import { formattedToNumber, numberToFormatted, trimTextField, setLimitedByteText, changeStartEndStringTimeZone, } from '.';
/**
 * 자식 component에서 v-model 사용을 위해 formData property computed로 변환하기
 * @param formData
 * @param fieldName fieldName
 * @param emit
 * @returns computed object
 */
export const useModelWrapper = (formData, fieldName, emit) => computed({
    get: () => {
        const splited = fieldName.split('.');
        const [first, second,] = splited;
        if (splited.length === 1) {
            return formData[first];
        }
        return formData[first][second];
    },
    set: (value) => emit('setFormData', fieldName, value),
});
/**
 * composition api에서 자식 component v-model 사용을 위해 formData number type property computed로 변환하기
 * @param formData
 * @param fieldName fieldName
 * @param emit
 * @returns computed object
 */
export const useNumberTypeModalWrapper = (formData, fieldName, emit) => computed({
    get() {
        if (formData[fieldName] === null) {
            return '';
        }
        return numberToFormatted(formData[fieldName]);
    },
    set(value) {
        if (value === '') {
            return emit('setFormData', fieldName, null);
        }
        return emit('setFormData', fieldName, formattedToNumber(value));
    },
});
/**
 * 허브 셀렉터에 따라 페이지 타이틀 변함
 * @param _vuexStore 페이지 타이틀 변경 이후에 실행할 함수
 * @param fn 페이지 타이틀 변경 이후에 실행할 함수
 * @returns
 */
export const autoRefreshPageTitle = (_vuexStore, fn = () => { }) => watch(() => _vuexStore.getters['hubSelector/getterSubscribeHubId'], () => {
    _vuexStore.dispatch('app/setPageTitle');
    return fn();
});
/**
 * get props item property
 * @param {Object} item composition api ref obj
 * @param {string} field
 * @returns
 */
const makeItemToComputed = (item, field) => computed(() => {
    const splitedField = field.split('.');
    const [first, second,] = splitedField;
    if (second) {
        return item.value[first][second];
    }
    return item.value[first];
});
const setFormData = (formData, key, value) => {
    const originData = formData;
    const splited = key.split('.');
    const [first, second, third,] = splited;
    if (third) {
        originData[first][second][third] = value;
        return;
    }
    if (second) {
        originData[first][second] = value;
        return;
    }
    originData[first] = value;
};
/**
 * formData property computed로 변환하기
 * @param {Object} formData
 * @param {string} fieldName fieldName
 * @returns {Object} computed object
 */
const makeFormDataComputed = (formData, fieldName) => computed({
    get: () => {
        const splited = fieldName.split('.');
        const [first, second, third,] = splited;
        if (third) {
            return formData[first][second][third];
        }
        if (second) {
            return formData[first][second];
        }
        return formData[first];
    },
    set: (value) => setFormData(formData, fieldName, value),
});
/**
 * setFormData utils을 사용해 formData number type property computed로 변환하기
 * @param {Object} formData
 * @param {string} fieldName fieldName
 * @returns {Object} computed object
 */
const makeNumberTypeFormDataComputed = (formData, fieldName) => computed({
    get() {
        if (formData[fieldName] === null) {
            return '';
        }
        return numberToFormatted(formData[fieldName]);
    },
    set(value) {
        if (value === '') {
            return setFormData(formData, fieldName, null);
        }
        return setFormData(formData, fieldName, formattedToNumber(value));
    },
});
/**
 * v-text-field onBlur event 시 setFormData을 통해 해당 text field value trim하기
 * @param {Object} formData
 * @param {string} fieldName
 * @param {*} fieldValue
 */
const handleBlurTrimTextFormData = (formData, fieldName, fieldValue) => {
    setFormData(formData, fieldName, trimTextField(fieldValue));
};
/**
 * byte limited formData property computed
 * @param formData
 * @param fieldName fieldName
 * @param maxByte
 * @param ref ref obj
 * @returns computed object
 */
export const makeByteLimitedFormDataComputed = (formData, fieldName, maxByte, ref) => computed({
    get: () => {
        const splited = fieldName.split('.');
        const [first, second,] = splited;
        if (splited.length === 1) {
            return formData[first];
        }
        return formData[first][second];
    },
    set: (value) => {
        const result = setLimitedByteText(value, maxByte, ref.value);
        setFormData(formData, fieldName, result);
    },
});
/**
 * set API res workingTime string data to view layer object data
 * @param workingTime
 * @returns workingTime object
 */
const makeWorkingTimeStringToObject = (workingTime) => {
    const krTimeString = changeStartEndStringTimeZone(workingTime, timeZoneCode.KR);
    const slicedWorkingTimeOn = krTimeString.slice(0, 4);
    const on = [
        slicedWorkingTimeOn.slice(0, 2),
        slicedWorkingTimeOn.slice(2, 4),
    ].join(':');
    const slicedWorkingTimeOff = krTimeString.slice(4, 8);
    const off = [
        slicedWorkingTimeOff.slice(0, 2),
        slicedWorkingTimeOff.slice(2, 4),
    ].join(':');
    return {
        on,
        off,
    };
};
export const prependModuleName = (moduleName, actionTypes) => {
    const result = {};
    Object.values(actionTypes).forEach((v) => {
        result[v] = `${moduleName}/${v}`;
    });
    return result;
};
/**
 * 차트 캡처
 * @throws {BusinessError} 차트 인스턴스가 없으면 오류 발생
 */
export const saveAsImage = (chartRef, fileName = 'chart') => {
    if (!chartRef.value) {
        throw new BusinessError('차트 인스턴스가 없습니다.');
    }
    const imgSrc = chartRef.value.getDataURL({
        type: 'png',
        pixelRatio: 2,
    });
    const link = document.createElement('a');
    link.href = imgSrc;
    link.download = `${fileName}.png`;
    link.click();
};
export { makeItemToComputed, setFormData, makeFormDataComputed, makeNumberTypeFormDataComputed, handleBlurTrimTextFormData, makeWorkingTimeStringToObject, };
